import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { observable } from "mobx";

import { MedicationGroup, PermissionBit } from "@/api";

import { MedicationDto } from "../../../api/api-connector";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { dayNames } from "../../../utils/data-utils";
import { getServerTodayISO } from "../../../utils/server-time";
import { everyNthDay } from "../schedule-utils";

export class Medications extends DataModel<MedicationDto> {
  @observable accessor switched = false;
  switchedOrder = [1, 2, 0];
  origOrder = [2, 0, 1];

  @observable accessor changeId = null;
  @observable accessor changeDosageField: any = {
    medication_dosage: [],
    medication_change_date: getServerTodayISO(),
  };

  constructor(
    data: MedicationDto[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);
    this.hasHistory = true;

    // access
    this.access = {
      editRules: {
        medical: AccessRules.owner,
        monitoring: AccessRules.visible,
      },
      deleteRules: {
        medical: AccessRules.none,
        monitoring: AccessRules.visible,
      },
      historyRequiresMonitoring: true,
    };
  }

  canEditName(member: MedicationDto) {
    // The medication spec types `medication_group` as a plain string, so
    // compare against the enum's string value rather than the enum member.
    return member.medication_group === (MedicationGroup.Other as string);
  }

  freqDays(member: MedicationDto) {
    return (member.medication_frequency_on_days ?? [])
      .map((day) => i18n._(dayNames[day]))
      .join("/");
  }

  freqString(member: MedicationDto) {
    const repeatEvery = member.medication_frequency_regular_repeat_every;
    if (repeatEvery != null) {
      return everyNthDay(repeatEvery);
    } else {
      return i18n._(msg`Days: ${this.freqDays(member)}`);
    }
  }

  startChangingDosage(item: MedicationDto) {
    // The set the inline editors are gated on — everything the change-dosage
    // endpoint accepts. Dose and unit belong here too: adapting a dose is as
    // often a change of strength ("5 mg" -> "10 mg") as of how many are taken.
    this.startEditingItem(item, [
      "medication_dose",
      "medication_unit",
      "medication_dosage",
      "medication_frequency_regular_repeat_every",
    ]);

    this.editingDraft["medication_change_date"] = getServerTodayISO();
  }

  isEditingItem(item: MedicationDto) {
    const idAttr = this.idAttr;
    return (
      this.editingItemId !== undefined &&
      idAttr != null &&
      this.editingItemId === item[idAttr]
    );
  }

  stopChangingDosage() {
    this.changeId = null;
    this.changeDosageField = {
      medication_dosage: [],
      medication_change_date: getServerTodayISO(),
    };
  }
}
